// =============================================================================
// TASK-4720 follow-up (dispatcher 2026-10-22) — OFFLINE TIME-OFF OUTBOX.
// =============================================================================
// Every time-off write is now the saveTimeOffRequest callable (the overlap
// guard needs the server), and a callable cannot run with no signal. In the
// oilfield no signal is normal, so a request or a withdraw made offline is kept
// on THIS phone as "waiting to send" and sent automatically when the connection
// returns. If the server then refuses it (overlap, approved meanwhile, ...) the
// refusal is shown and the draft is KEPT so the driver can fix it. Nothing is
// dropped silently: an entry leaves the outbox only when the server accepted it
// or the driver discarded it.
//
// Same shape as the existing offline queues: the job-board localStorage outbox
// (TASK-4666: write durably, try now, re-flush later, re-read before writing
// back so a row queued mid-flush is never lost) scoped like the DVIR photo
// queue (TASK-4604: per TENANT + per PERSON, never a shared bucket, and no
// drain without an identity — a shared yard tablet holds several drivers).
//
// The sender is injected (dispatch.js registers httpsCallable
// 'saveTimeOffRequest'), so this file imports nothing and runs unchanged
// under node for tests/task4720-timeoff-outbox.unit.cjs.
//
// Duplicate safety: every queued create carries a clientRequestId; the server
// returns the existing request when one with that id already exists, so a send
// whose reply was lost on a dying connection never makes a second request.
// =============================================================================
(function (root) {
  'use strict';

  var PREFIX = 'dd_timeoff_outbox_v1::';
  var MAX_ITEMS = 50;

  function storage() {
    try { return root.localStorage || null; } catch (_) { return null; }
  }
  function key(scope) { return PREFIX + scope; }
  function scopeOf(tenantId, personId) {
    if (!tenantId || !personId) return null; // no identity -> refuse, never a shared bucket
    return String(tenantId) + '::' + String(personId);
  }
  function read(scope) {
    var ls = storage();
    if (!ls || !scope) return [];
    try {
      var arr = JSON.parse(ls.getItem(key(scope)) || '[]');
      return Array.isArray(arr) ? arr.filter(function (x) { return x && x.qid; }) : [];
    } catch (_) { return []; }
  }
  function write(scope, arr) {
    var ls = storage();
    if (!ls || !scope) return false;
    try {
      if (!arr.length) ls.removeItem(key(scope));
      else ls.setItem(key(scope), JSON.stringify(arr));
      return true;
    } catch (_) { return false; }
  }
  function newId(prefix) {
    return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  // A transport failure (no signal / dropped connection), as opposed to the
  // server answering. Firebase callables report a failed fetch as code
  // 'internal' with the bare message 'internal'; the server's own internal
  // errors always carry a sentence, so the bare message is the tell.
  function isOfflineError(err, nav) {
    var n = nav || root.navigator;
    if (n && n.onLine === false) return true;
    var code = String((err && err.code) || '').replace(/^functions\//, '');
    if (code === 'unavailable' || code === 'deadline-exceeded') return true;
    var msg = String((err && err.message) || '').trim().toLowerCase();
    if (code === 'internal' && (msg === 'internal' || msg === '')) return true;
    if (/failed to fetch|network ?error|load failed|internet disconnected/.test(msg)) return true;
    return false;
  }

  /**
   * Queue a write. kind: 'create' | 'update' | 'withdraw'. payload: the exact
   * saveTimeOffRequest data. Returns the stored entry, or null when it could
   * not be stored (the caller must then tell the driver it was NOT saved).
   */
  function enqueue(scope, kind, payload, opts) {
    if (!scope) return null;
    var arr = read(scope);
    var p = Object.assign({}, payload || {});
    if (kind === 'create' && !p.clientRequestId) p.clientRequestId = newId('c_');
    var entry = {
      qid: (opts && opts.qid) || newId('q_'),
      kind: kind,
      payload: p,
      state: 'waiting',
      error: null,
      queuedAt: new Date().toISOString(),
    };
    var idx = -1;
    for (var i = 0; i < arr.length; i++) if (arr[i].qid === entry.qid) idx = i;
    if (idx >= 0) {
      entry.queuedAt = arr[idx].queuedAt;
      if (kind === 'create' && arr[idx].payload && arr[idx].payload.clientRequestId) entry.payload.clientRequestId = arr[idx].payload.clientRequestId;
      arr[idx] = entry;
    } else {
      if (arr.length >= MAX_ITEMS) return null; // never evict a driver's draft to make room
      arr.push(entry);
    }
    return write(scope, arr) ? entry : null;
  }

  function list(scope) { return read(scope); }

  function remove(scope, qid) {
    var arr = read(scope);
    var out = arr.filter(function (x) { return x.qid !== qid; });
    if (out.length === arr.length) return false;
    return write(scope, out);
  }

  /** The queued withdraw for a server request id, if any. */
  function withdrawFor(scope, requestId) {
    var arr = read(scope);
    for (var i = 0; i < arr.length; i++) if (arr[i].kind === 'withdraw' && arr[i].payload && arr[i].payload.requestId === requestId) return arr[i];
    return null;
  }

  var flushing = {};
  /**
   * Send every WAITING entry, oldest first, through sender(payload).
   *   accepted          -> removed
   *   server refusal    -> kept, state 'refused', error = the server's sentence
   *   transport failure -> kept waiting; the flush stops (no signal for the rest)
   * Refused entries are not re-sent until the driver edits them (which puts them
   * back to waiting). Returns { sent: [entry], refused: [entry], waiting: n }.
   */
  function flush(scope, sender, opts) {
    var o = opts || {};
    if (!scope || typeof sender !== 'function') return Promise.resolve({ sent: [], refused: [], waiting: 0, skipped: true });
    if (flushing[scope]) return flushing[scope];
    var sent = []; var refused = [];
    var run = (async function () {
      var todo = read(scope).filter(function (x) { return x.state === 'waiting'; });
      for (var i = 0; i < todo.length; i++) {
        var item = todo[i];
        try {
          var res = await sender(item.payload, item);
          remove(scope, item.qid);
          sent.push(Object.assign({}, item, { result: res && res.data !== undefined ? res.data : res }));
        } catch (err) {
          if (isOfflineError(err, o.navigator)) break;
          // Re-read before writing back: a new entry may have been queued mid-flush.
          var cur = read(scope);
          for (var j = 0; j < cur.length; j++) {
            if (cur[j].qid === item.qid) {
              cur[j].state = 'refused';
              cur[j].error = String((err && err.message) || 'The office system refused this request.').slice(0, 300);
              cur[j].refusedAt = new Date().toISOString();
              refused.push(cur[j]);
            }
          }
          write(scope, cur);
        }
      }
      return { sent: sent, refused: refused, waiting: read(scope).filter(function (x) { return x.state === 'waiting'; }).length };
    })();
    flushing[scope] = run;
    return run.then(function (r) { delete flushing[scope]; return r; }, function (e) { delete flushing[scope]; throw e; });
  }

  var api = {
    scopeOf: scopeOf,
    isOfflineError: isOfflineError,
    enqueue: enqueue,
    list: list,
    remove: remove,
    withdrawFor: withdrawFor,
    flush: flush,
    _key: key,
  };
  root.TimeOffOutbox = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
